import type { AnalysisMetric } from "./analysis-columns";
import { CalendarRange } from "lucide-react";
import type { AnalysisCalendarPeriod } from "./analysis-date-range";
import { ProductionAnalysisLoading } from "./ProductionAnalysisLoading";
import type { ShiftFormManagerDashboard, ShiftFormManagerDashboardFilters } from "../../../shared/shift-production-form/contracts";
import { analysisNumber } from "./analysis-presentation";
import { buildFactorySummary, type FactoryMetric, type FactorySummary } from "./analysis-factory-summary";
import { shiftLabels } from "../shift-production-form/line-options";

export type ProductionAnalysisFactorySummaryProps = {
  current: ShiftFormManagerDashboard | null;
  previous: ShiftFormManagerDashboard | null;
  filters: ShiftFormManagerDashboardFilters;
  calendarPeriod?: AnalysisCalendarPeriod;
  phase: "loading" | "ready" | "error";
  previousPhase: "loading" | "ready" | "error";
  comparisonAvailability?: "complete" | "incomplete" | "unknown" | "multi-day";
  refreshing?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onMissing?: (metric: AnalysisMetric) => void;
};

function comparisonText(metric: FactoryMetric, previousPhase: ProductionAnalysisFactorySummaryProps["previousPhase"], hasPreviousPeriod: boolean) {
  if (metric.state === "UNAVAILABLE") return "Özet alınamadı";
  if (metric.state === "EMPTY") return "Kayıt yok";
  if (metric.state === "MISSING") return "Ölçüm eksik";
  if (!hasPreviousPeriod) return null;
  if (previousPhase === "loading") return null;
  if (previousPhase === "error") return "Önceki dönem alınamadı";
  if (!metric.comparison || metric.comparison.kind === "NO_PREVIOUS") return null;
  if (metric.comparison.kind === "PREVIOUS_EMPTY") return "Önceki dönemde kayıt yok";
  if (metric.comparison.kind === "PREVIOUS_MISSING") return "Önceki dönem karşılaştırılamıyor";
  if (metric.comparison.direction === "SAME") return "Önceki dönemle aynı";
  return `%${analysisNumber(Math.abs(metric.comparison.percent))} ${metric.comparison.direction === "UP" ? "artış" : "azalış"}`;
}

function comparisonTone(metric: FactoryMetric, previousPhase: ProductionAnalysisFactorySummaryProps["previousPhase"]) {
  if (previousPhase !== "ready" || metric.state !== "RECORDED" || metric.comparison?.kind !== "CHANGE" || metric.comparison.direction === "SAME") return "neutral";
  const improved = metric.id === "OUTPUT" ? metric.comparison.direction === "UP" : metric.comparison.direction === "DOWN";
  return improved ? "positive" : "negative";
}

function metricValue(metric: FactoryMetric) {
  return metric.state === "RECORDED" && metric.value !== null ? <><strong>{analysisNumber(metric.value)}</strong><small>{metric.unit}</small></> : <strong>{metric.state === "EMPTY" ? "—" : "Bilinmiyor"}</strong>;
}

export function ProductionAnalysisFactorySummary({ current, previous, filters, calendarPeriod, phase, previousPhase, refreshing, error, onRetry, onMissing, comparisonAvailability }: ProductionAnalysisFactorySummaryProps) {
  if (phase === "loading") return <section className="pa-factory-summary" aria-label="Üretim özeti" aria-busy="true"><ProductionAnalysisLoading mode="summary" /></section>;
  if (phase === "error") return <section className="pa-factory-summary" aria-label="Üretim özeti" role="alert"><p className="pa-factory-caption">Genel özet alınamadı{error ? `: ${error}` : "."}</p>{onRetry ? <button type="button" onClick={onRetry}>Tekrar dene</button> : null}</section>;
  const summary: FactorySummary = buildFactorySummary(current, previousPhase === "ready" ? previous : null, filters, calendarPeriod);
  if (summary.metrics.every(metric => metric.state === "EMPTY")) return <section className="pa-factory-summary" aria-label="Üretim özeti" aria-busy={Boolean(refreshing)}>
    <div className="pa-summary-empty" role="status">
      <CalendarRange size={24} strokeWidth={1.4} aria-hidden="true" />
      {summary.periodLabel ? <p className="pa-empty-context">{summary.periodLabel}</p> : null}
      <h3>Bu dönemde kayıt yok</h3>
      <p>Genel özet için üstteki tarih aralığını değiştirin.</p>
    </div>
  </section>;
  const scope = [summary.caption, summary.periodLabel, summary.filters.shift ? shiftLabels[summary.filters.shift] : "Tüm vardiyalar", summary.previousPeriodLabel ? `Karşılaştırma: ${summary.previousPeriodLabel}` : null].filter(Boolean).join(" · ");
  return <section className="pa-factory-summary" aria-label="Üretim özeti" aria-busy={Boolean(refreshing)}>
    <p className="pa-summary-scope">{summary.caption}</p>{comparisonAvailability === "multi-day" ? <p className="pa-summary-scope">Kıyas onaylı verilerle yapılır; dönem kapsamı doğrulanmadı.</p> : null}<p className="sr-only">{scope}</p>
    <div className="pa-factory-metrics">
      {summary.metrics.map(metric => <article key={metric.id} className="pa-factory-metric">
        <span>{metric.label}</span>
        <div>{metricValue(metric)}</div>
        <small className="pa-factory-comparison" data-trend={comparisonAvailability === "incomplete" || comparisonAvailability === "unknown" ? "neutral" : comparisonTone(metric, previousPhase)}>{metric.state !== "RECORDED" ? comparisonText(metric, previousPhase, Boolean(summary.previousFilters)) : comparisonAvailability === "incomplete" ? "Tüm hat ve vardiyalar doğrulanamadı; kıyas gösterilmiyor" : comparisonAvailability === "unknown" ? "Kapsam doğrulanamadı; kıyas gösterilmiyor" : comparisonText(metric, previousPhase, Boolean(summary.previousFilters))}</small>
        {metric.state === "MISSING" && onMissing ? <button type="button" className="pa-missing-link" onClick={() => onMissing(metric.id === "SCRAP" ? "WASTE" : metric.id)}>Eksik kayıtları gör</button> : null}
      </article>)}
    </div>
  </section>;
}
